/* eslint-disable no-magic-numbers -- tuned wheel navigation constants */
const SERIES_GAP_MS = 200;
const VALID_SERIES_MIN_EVENTS = 5;
const BOOST_WINDOW_MS = 1500;
const BOOST_STEP = 0.5;
const MAX_BOOST = 3.0;
const VELOCITY_GAIN = 0.3;
const VELOCITY_DECAY_PER_FRAME = 0.8;
const MIN_VELOCITY = 0.05;
const MIN_EVENTS_FOR_INERTIA = 2;
const INERTIA_DELAY_MS = 50;

/**
 * Wheel navigation along an alignment: series of rapid wheel events less than 1.5 s
 * apart accelerate, and mouse wheel (not trackpad) series keep gliding for a few
 * frames after the last event.
 */
export class ScrollNavigationInertia {
  /** When disabled, each wheel event moves by exactly its speed: no boost and no glide. */
  public enabled = true;
  /** Minimum scroll speed to trigger inertia (trackpad ~0.3, mouse wheel ~1.0+) */
  public minSpeed = 0.5;

  #velocity = 0;
  #lastTime = 0;
  #eventCount = 0;
  #seriesEventCount = 0;
  #lastSeriesEndTime = 0;
  #lastSeriesWasValid = false;
  #accumulatedBoost = 1.0;

  /**
   * Registers a wheel event and returns the distance to navigate for it.
   * @param speed signed wheel speed, already flipped when looking backwards
   */
  public registerScroll(speed: number, now: number): number {
    if (!this.enabled) {
      this.reset();
      return speed;
    }
    const magnitude = Math.abs(speed);
    const timeSinceLastScroll = now - this.#lastTime;
    const timeSinceLastSeries = now - this.#lastSeriesEndTime;

    if (timeSinceLastScroll > SERIES_GAP_MS) {
      if (this.#seriesEventCount >= VALID_SERIES_MIN_EVENTS) {
        this.#lastSeriesEndTime = this.#lastTime;
        this.#lastSeriesWasValid = true;
      }

      this.#seriesEventCount = magnitude;

      if (this.#lastSeriesWasValid && timeSinceLastSeries < BOOST_WINDOW_MS) {
        this.#accumulatedBoost = Math.min(
          this.#accumulatedBoost + BOOST_STEP,
          MAX_BOOST
        );
      } else {
        this.#accumulatedBoost = (1.0 + this.#accumulatedBoost) / 2;
        this.#lastSeriesWasValid = false;
      }

      this.#eventCount = magnitude >= this.minSpeed ? magnitude : 0;
      this.#velocity = 0;
    } else {
      this.#seriesEventCount += magnitude;
      // Trackpads (speed ~0.3) already have OS-level momentum, so only mouse wheels accumulate inertia.
      if (magnitude >= this.minSpeed) {
        this.#eventCount += magnitude;
        this.#velocity += speed * VELOCITY_GAIN * this.#accumulatedBoost;
      } else if (this.#velocity !== 0) {
        // A low-speed event after high-speed ones is a trackpad momentum tail.
        this.stop();
      }
    }

    this.#lastTime = now;
    return speed * this.#accumulatedBoost;
  }

  /** Returns the distance to glide during this frame, or 0 when there is no inertia. */
  public nextInertiaDelta(now: number): number {
    if (
      this.enabled &&
      this.#eventCount > MIN_EVENTS_FOR_INERTIA &&
      Math.abs(this.#velocity) > MIN_VELOCITY &&
      now - this.#lastTime > INERTIA_DELAY_MS
    ) {
      const delta = this.#velocity;
      this.#velocity *= VELOCITY_DECAY_PER_FRAME;
      if (Math.abs(this.#velocity) <= MIN_VELOCITY) {
        this.stop();
      }
      return delta;
    }
    if (!this.enabled && this.#velocity !== 0) {
      this.stop();
    }
    return 0;
  }

  /** Stops the current glide but keeps the series boost. */
  public stop(): void {
    this.#velocity = 0;
    this.#eventCount = 0;
  }

  /** Forgets everything, as if no wheel event was ever received. */
  public reset(): void {
    this.stop();
    this.#lastTime = 0;
    this.#seriesEventCount = 0;
    this.#lastSeriesEndTime = 0;
    this.#lastSeriesWasValid = false;
    this.#accumulatedBoost = 1.0;
  }
}
